import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Input,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import deLocale from "date-fns/locale/de";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import axios from "axios";
import { API_BASE_URL } from "../config";

// BidEditDialog Komponente
const BidEditDialog = ({ open, onClose, bid, onSave }) => {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (bid) {
      setAmount(bid.amount);
    }
  }, [bid]);

  const handleSubmit = (e) => {
    // Add this function
    e.preventDefault();
    if (!amount || amount <= 0) {
      setError("Bitte geben Sie einen gültigen Betrag ein");
      return;
    }
    onSave({ ...bid, amount: parseFloat(amount) });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Gebot bearbeiten</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel htmlFor="amount">Betrag</InputLabel>
            <Input
              id="amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              endAdornment={<InputAdornment position="end">€</InputAdornment>}
              error={!!error}
              inputProps={{ min: 0, step: "0.01" }}
            />
            {error && (
              <span className="MuiFormHelperText-root Mui-error">{error}</span>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="contained" color="primary">
            Speichern
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// Bidding Service
const bidService = {
  getTrikotBids: async (trikotId) => {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${API_BASE_URL}/api/bids/trikot/${trikotId}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  },
  deleteBid: async (bidId) => {
    const token = localStorage.getItem("token");
    const response = await axios.delete(`${API_BASE_URL}/api/bids/${bidId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  updateBid: async (bidId, amount) => {
    const token = localStorage.getItem("token");
    const response = await axios.put(
      `${API_BASE_URL}/api/bids/${bidId}`,
      { amount },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },
};

const TrikotFormDialog = ({ open, onClose, onSave, trikot }) => {
  const [formData, setFormData] = useState({
    playerName: "",
    number: "",
    startingBid: "",
    endTime: new Date("2024-12-20T22:00:00"),
    imageUrl: "",
    active: true,
  });

  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [bids, setBids] = useState([]);
  const [selectedBid, setSelectedBid] = useState(null);
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [loadingBids, setLoadingBids] = useState(false);

  const fetchBids = useCallback(async () => {
    if (!trikot?._id) return;

    try {
      setLoadingBids(true);
      console.log("Fetching bids for trikot:", trikot._id); // Debug-Log
      const response = await bidService.getTrikotBids(trikot._id);
      setBids(response.bids);
    } catch (error) {
      console.error("Error fetching bids:", error); // Debug-Log
      setErrors((prev) => ({
        ...prev,
        bids: "Fehler beim Laden der Gebote",
      }));
    } finally {
      setLoadingBids(false);
    }
  }, [trikot?._id]);

  useEffect(() => {
    if (trikot?._id) {
      setFormData({
        playerName: trikot.playerName,
        number: String(trikot.number), // Convert to string
        startingBid: trikot.startingBid,
        endTime: new Date(trikot.endTime),
        imageUrl: trikot.imageUrl,
        active: trikot.active || true,
      });
      fetchBids();
    } else {
      setFormData({
        playerName: "",
        number: "", // Make sure this is an empty string
        startingBid: "",
        endTime: new Date("2024-12-20T22:00:00"),
        imageUrl: "",
        active: true,
      });
      setBids([]);
    }
  }, [trikot, fetchBids]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.playerName.trim()) {
      newErrors.playerName = "Spielername ist erforderlich";
    }

    if (!formData.number.trim()) {
      newErrors.number = "Trikotnummer ist erforderlich";
    }

    if (formData.startingBid && parseFloat(formData.startingBid) <= 0) {
      newErrors.startingBid =
        "Wenn angegeben, muss das Startgebot größer als 0 sein";
    }

    if (!formData.imageUrl.trim()) {
      newErrors.imageUrl = "Bild-Pfad ist erforderlich";
    }

    if (!formData.endTime) {
      newErrors.endTime = "Enddatum ist erforderlich";
    } else if (formData.endTime <= new Date()) {
      newErrors.endTime = "Enddatum muss in der Zukunft liegen";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "number" ? String(value) : value, // Ensure number is always a string
    }));
    // Clear errors for this field when it changes
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Prepare the data according to the schema requirements
      const submitData = {
        ...formData,
        number: String(formData.number), // Ensure number is a string
        startingBid: formData.startingBid
          ? parseFloat(formData.startingBid)
          : 0, // Make optional
        active: true,
      };

      await onSave(submitData);
      setSuccess("Trikot erfolgreich gespeichert");
      onClose();
    } catch (error) {
      setErrors({
        submit:
          error.response?.data?.message || "Fehler beim Speichern des Trikots",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBid = async (bidId) => {
    if (!window.confirm("Möchten Sie dieses Gebot wirklich löschen?")) {
      return;
    }

    try {
      await bidService.deleteBid(bidId);
      await fetchBids();
      setSuccess("Gebot erfolgreich gelöscht");
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        bids: "Fehler beim Löschen des Gebots",
      }));
    }
  };

  const handleEditBid = (bid) => {
    setSelectedBid(bid);
    setBidDialogOpen(true);
  };

  const handleSaveBid = async (updatedBid) => {
    try {
      await bidService.updateBid(updatedBid._id, updatedBid.amount);
      await fetchBids();
      setBidDialogOpen(false);
      setSuccess("Gebot erfolgreich aktualisiert");
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        bids: "Fehler beim Aktualisieren des Gebots",
      }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {trikot ? "Trikot bearbeiten" : "Neues Trikot hinzufügen"}
        </DialogTitle>

        <DialogContent>
          {success && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => setSuccess("")}
            >
              {success}
            </Alert>
          )}

          {errors.submit && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() =>
                setErrors((prev) => ({ ...prev, submit: undefined }))
              }
            >
              {errors.submit}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                name="playerName"
                label="Spielername"
                fullWidth
                value={formData.playerName}
                onChange={handleChange}
                error={!!errors.playerName}
                helperText={errors.playerName}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                name="number"
                label="Trikotnummer"
                fullWidth
                value={formData.number}
                onChange={handleChange}
                error={!!errors.number}
                helperText={errors.number}
                disabled={loading}
                type="text" // Change to text type to ensure string handling
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.startingBid}>
                <InputLabel htmlFor="startingBid">
                  Startgebot (Optional)
                </InputLabel>
                <Input
                  id="startingBid"
                  name="startingBid"
                  type="number"
                  value={formData.startingBid}
                  onChange={handleChange}
                  disabled={loading}
                  endAdornment={
                    <InputAdornment position="end">€</InputAdornment>
                  }
                  inputProps={{ min: 0, step: "0.01" }}
                />
                {errors.startingBid && (
                  <span className="MuiFormHelperText-root Mui-error">
                    {errors.startingBid}
                  </span>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="imageUrl"
                label="Bild-URL"
                fullWidth
                value={formData.imageUrl}
                onChange={handleChange}
                error={!!errors.imageUrl}
                helperText={
                  errors.imageUrl ||
                  "Geben Sie einen kompletten Bild-URL ein (http://...). Das Bild wird automatisch optimiert und lokal gespeichert."
                }
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={deLocale}
              >
                <DateTimePicker
                  label="Enddatum"
                  value={formData.endTime}
                  onChange={(newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      endTime: newValue,
                    }));
                    if (errors.endTime) {
                      setErrors((prev) => ({
                        ...prev,
                        endTime: undefined,
                      }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={!!errors.endTime}
                      helperText={errors.endTime}
                    />
                  )}
                  disabled={loading}
                  minDateTime={new Date()}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>

          {/* Gebotsliste */}
          {trikot && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Gebote
              </Typography>

              {errors.bids && (
                <Alert
                  severity="error"
                  sx={{ mb: 2 }}
                  onClose={() =>
                    setErrors((prev) => ({ ...prev, bids: undefined }))
                  }
                >
                  {errors.bids}
                </Alert>
              )}

              {loadingBids ? (
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Bieter</TableCell>
                        <TableCell align="right">Betrag</TableCell>
                        <TableCell align="right">Datum</TableCell>
                        <TableCell align="right">Aktionen</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bids.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            Keine Gebote vorhanden
                          </TableCell>
                        </TableRow>
                      ) : (
                        bids.map((bid) => (
                          <TableRow key={bid._id}>
                            <TableCell>{bid.userId.username}</TableCell>
                            <TableCell align="right">
                              {bid.amount.toFixed(2)}€
                            </TableCell>
                            <TableCell align="right">
                              {new Date(bid.createdAt).toLocaleString("de-DE")}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => handleEditBid(bid)}
                                color="primary"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteBid(bid._id)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Abbrechen
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading
              ? "Wird gespeichert..."
              : trikot
              ? "Aktualisieren"
              : "Erstellen"}
          </Button>
        </DialogActions>
      </form>

      {/* Dialog zum Bearbeiten eines Gebots */}
      <BidEditDialog
        open={bidDialogOpen}
        onClose={() => setBidDialogOpen(false)}
        bid={selectedBid}
        onSave={handleSaveBid}
      />
    </Dialog>
  );
};

export default TrikotFormDialog;
